import { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getBestLanguage } from '../../utils/languageStorage';
import { 
  MessageSquare, 
  PenTool, 
  Code, 
  FileText, 
  Mail, 
  Image, 
  Database,
  CheckSquare,
  Menu,
  X,
  Sparkles,
  Languages,
  BookOpen,
  Brain,
  User,
  Share2,
  ChefHat,
  PiggyBank,
  Calendar,
  Heart,
  Users,
  FileEdit,
  ImageIcon
} from "lucide-react";
import { Button } from "../ui/button";
import { ThemeToggle } from "../ui/theme-toggle";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { cn } from "../../lib/utils";

/**
 * Normalize i18n language codes to URL-compatible codes
 */
const normalizeLangCode = (langCode: string): string => {
  const normalized = langCode.toLowerCase().split('-')[0];
  switch (normalized) {
    case 'zh':
      return 'cn';
    default:
      return normalized;
  }
};

const navigationItems = [
  { title: "Dashboard", url: "/dashboard", icon: Sparkles },
  { title: "AI Chat", url: "/ai-chat", icon: MessageSquare },
  { title: "Content Writer", url: "/ai-writer", icon: PenTool },
  { title: "Code Generator", url: "/ai-code", icon: Code },
  { title: "Document Summary", url: "/ai-summary", icon: FileText },
  { title: "Email Assistant", url: "/ai-email", icon: Mail },
  { title: "OCR Tool", url: "/ai-ocr", icon: Image },
  { title: "SQL Builder", url: "/ai-sql", icon: Database },
  { title: "Grammar Check", url: "/ai-grammar", icon: CheckSquare },
  { title: "Translation", url: "/ai-translator", icon: Languages },
  { title: "Story Writer", url: "/ai-story", icon: BookOpen },
  { title: "Quiz Generator", url: "/ai-quiz", icon: Brain },
  { title: "Resume Builder", url: "/ai-resume", icon: User },
  { title: "Social Media", url: "/ai-social", icon: Share2 },
  { title: "Recipe Generator", url: "/ai-recipe", icon: ChefHat },
  { title: "Finance Helper", url: "/ai-finance", icon: PiggyBank },
  { title: "Meeting Notes", url: "/ai-meeting", icon: Calendar },
  { title: "Sentiment Analysis", url: "/ai-sentiment", icon: Heart },
  { title: "Interview Prep", url: "/ai-interview", icon: Users },
  { title: "Image Generator", url: "/ai-image-generator", icon: ImageIcon },
];

interface SidebarProps {
  className?: string;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ className, isMobile = false, isOpen = false, onClose }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { lang } = useParams();
  const { i18n } = useTranslation();
  
  // Get current language with fallback chain
  const getCurrentLanguage = (): string => {
    if (lang) {
      return lang;
    }
    if (i18n.language) {
      return normalizeLangCode(i18n.language);
    }
    try {
      return getBestLanguage();
    } catch (error) {
      console.warn('Failed to get best language from storage:', error);
    }
    return 'en';
  };

  const currentLang = getCurrentLanguage();

  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-sidebar-border">
        {(!isCollapsed || isMobile) && (
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-ai-primary" />
            <span className="text-lg font-bold ai-gradient-text">AI Suite</span>
          </div>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hover:bg-sidebar-accent text-sidebar-foreground"
          >
            {isCollapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className={cn(
        "flex-1 p-4 space-y-2 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-sidebar-accent/40 hover:scrollbar-thumb-sidebar-accent/60",
        isCollapsed && !isMobile && "px-2"
      )}>
        {navigationItems.map((item) => (
          <NavLink
            key={item.title}
            to={`/${currentLang}${item.url}`}
            onClick={isMobile ? onClose : undefined}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:scale-[1.02]",
                isActive 
                  ? "bg-gradient-to-r from-ai-primary/20 to-ai-secondary/20 text-ai-primary border border-ai-primary/40 shadow-lg shadow-ai-primary/20" 
                  : "text-sidebar-foreground/70 hover:text-sidebar-foreground",
                isCollapsed && !isMobile && "justify-center px-2 py-3 mx-1",
                isMobile && "text-base"
              )
            }
            title={isCollapsed && !isMobile ? item.title : undefined}
          >
            <item.icon className={cn(
              "flex-shrink-0",
              isCollapsed && !isMobile ? "w-5 h-5" : "w-5 h-5"
            )} />
            {(!isCollapsed || isMobile) && (
              <span className="text-sm font-medium truncate">{item.title}</span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className={cn(
        // Remove top border to avoid redundant line at sidebar bottom
        isCollapsed && !isMobile ? "p-2" : "p-4"
      )}>
        {/* <div className={cn(
          "flex items-center mb-3",
          isCollapsed && !isMobile ? "justify-center" : "justify-between"
        )}>
          <ThemeToggle />
          {(!isCollapsed || isMobile) && <span className="text-xs text-sidebar-foreground/60">Theme</span>}
        </div> */}
        {/* {(!isCollapsed || isMobile) && (
          <div className="bg-sidebar-accent/50 rounded-lg p-3 border border-sidebar-border">
            <p className="text-xs text-sidebar-foreground/70 text-center">
              Powered by Gemini AI
            </p>
          </div>
        )} */}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent 
          side="left" 
          className="w-72 p-0 border-sidebar-border bg-gradient-to-br from-background via-background/95 to-muted/30 backdrop-blur-xl"
        >
          <div className="flex flex-col h-full">
            <SidebarContent />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className={cn(
      "flex flex-col h-screen backdrop-blur-xl border-r border-sidebar-border transition-all duration-300",
      isCollapsed ? "w-16" : "w-64",
      className
    )} style={{ background: 'var(--gradient-sidebar)' }}>
      <SidebarContent />
    </div>
  );
}